import React, { useEffect } from 'react';
import useDashboardContext from 'contexts/dashboard.context';
import RequestStatusTag from 'components/base/request-status-tag';
import { ENUM_REQUEST_STATUS } from 'models/common.model';

interface RequestDetailsWrapperProps {
  codeId?: number | string;
  title?: string;
  titlePrefix?: string;
  status?: ENUM_REQUEST_STATUS;
  statusTag?: React.ReactNode;
  header?: string;
  subHeader?: string;
  pageTitle?: string;
  backLinkTitle?: string;
  headerExtra?: React.ReactNode;
  withCard?: boolean;
  enableBackLink?: boolean;
  children: React.ReactNode;
}

const RequestDetailsWrapper: React.FC<RequestDetailsWrapperProps> = ({
  codeId,
  title,
  titlePrefix = 'Request #',
  status,
  statusTag,
  header,
  subHeader,
  pageTitle = 'Track progress with procurement',
  backLinkTitle = 'Return to procurement',
  headerExtra,
  withCard = true,
  enableBackLink = true,
  children
}) => {
  const { setIsBackLink, setBackLinkTitle } = useDashboardContext();

  useEffect(() => {
    if (!enableBackLink) return;
    setIsBackLink(true);
    setBackLinkTitle(backLinkTitle);
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, [enableBackLink, setIsBackLink, setBackLinkTitle, backLinkTitle]);

  const resolvedTitle = title ?? (codeId !== undefined ? `${titlePrefix}${codeId}` : '');

  return (
    <div className='ml-2 md:ml-8 px-2 md:px-0'>
      {resolvedTitle && (
        <div className='flex items-center flex-wrap gap-2'>
          <h4 className='text-[20px] md:text-[24px] font-semibold text-[#2E3031] mr-2'>
            {resolvedTitle}
          </h4>
          {statusTag ?? (status ? <RequestStatusTag status={status} /> : null)}
        </div>
      )}
      {pageTitle && (
        <div className=' w-full md:max-w-[670px] flex justify-between items-center mb-6 md:mb-8'>
          {' '}
          <p className='text-[#6F7376] text-sm '>{pageTitle}</p> {headerExtra}
        </div>
      )}

      {withCard ? (
        <div className='border-4 border-[#F7F7F8] rounded py-[16px] md:py-[24px] w-full md:max-w-[670px]'>
          {(header || subHeader) && (
            <div className='flex flex-col md:flex-row md:justify-between px-[28px] items-start md:items-center'>
              <div className='pb-2 w-full'>
                {header && <p className='font-semibold text-[20px]'>{header}</p>}
                {subHeader && <p className='text-[#6F7376] text-sm'>{subHeader}</p>}
              </div>
            </div>
          )}
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
};

export default RequestDetailsWrapper;
